/**
 * `customAgentsApi` — standalone RTK Query slice for the proxy-server
 * Custom Agents surface: configs CRUD, run history, and the per-config
 * timeseries results.
 *
 * Owns its cache + tag space (`'CustomAgentConfig'` / `'CustomAgentRun'` /
 * `'CustomAgentTask'`) on top of `ontologyBaseQuery` + `PROXY_API_V1_URL`. The
 * proxy wire shape maps 1:1 onto the TS types (no wire-rename package).
 */

import { readWorkPages } from '../../problem-work/problem-work-api'
import type { WorkScope } from '../../problem-work/types'
import { createApi } from '@reduxjs/toolkit/query/react'

import { PROXY_API_V1_URL, ontologyBaseQuery } from '../../core'
import type { TagReading } from '../../tags'
import {
  normalizeTriggerStateSnapshot,
  type TriggerEvalResult,
  type TriggerStateSnapshot
} from '../../triggers'
import {
  denormalizeCreateCustomAgentConfigPayload,
  denormalizeUpdateCustomAgentConfigPayload,
  normalizeCustomAgentConfig,
  normalizeCustomAgentConfigList
} from './custom-agent-transform'
import type {
  CustomAgentBulkBatch,
  CustomAgentConfig,
  CustomAgentConfigEvaluation,
  CustomAgentResultItem,
  CustomAgentResultItemListPage,
  CustomAgentResultListPage,
  CustomAgentRun,
  CustomAgentRunMessage,
  CustomAgentTask,
  CreateCustomAgentConfigPayload,
  BatchUpdateCustomAgentResultsPayload,
  BatchUpdateCustomAgentResultsResponse,
  FollowUpCustomAgentRunPayload,
  PatchCustomAgentRunCompletionPayload,
  PatchCustomAgentResultFeedbackPayload,
  UpdateCustomAgentConfigPayload
} from './types'

export const CUSTOM_AGENTS_TAG_TYPES = [
  'CustomAgentConfig',
  'CustomAgentRun',
  'CustomAgentBulkBatch',
  'CustomAgentTask'
] as const
export type CustomAgentsTagType = (typeof CUSTOM_AGENTS_TAG_TYPES)[number]

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

interface BackendTagReading {
  time: string
  gateway_url?: string
  project?: string
  target_id?: string
  node_id?: string
  tag_path: string
  tag_type?: string
  value_double?: number | null
  value_int?: number | null
  value_bool?: boolean | null
  value_string?: string | null
  value_timestamp?: string | null
  value_json?: unknown
  quality?: string
  is_good?: boolean
}

function normalizeTagReading(response: BackendTagReading): TagReading {
  const valueDouble = response.value_double ?? null
  const valueInt = response.value_int ?? null
  const numeric = valueDouble !== null ? valueDouble : valueInt !== null ? valueInt : null
  return {
    time: response.time,
    gatewayUrl: response.gateway_url ?? '',
    project: response.project ?? '',
    targetId: response.target_id,
    nodeId: response.node_id ?? '',
    tagPath: response.tag_path,
    tagType: response.tag_type ?? '',
    valueDouble,
    valueInt,
    valueBool: response.value_bool ?? null,
    valueString: response.value_string ?? null,
    valueTimestamp: response.value_timestamp ?? null,
    valueJson: response.value_json ?? null,
    quality: response.quality ?? 'Good',
    isGood: response.is_good ?? true,
    value: numeric
  }
}

export const customAgentsApi = createApi({
  reducerPath: 'customAgentsApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: CUSTOM_AGENTS_TAG_TYPES,
  endpoints: (builder) => ({
    // ─── Custom-Agent Configs ─────────────────────────────────────────────
    listCustomAgentConfigs: builder.query<CustomAgentConfig[], void>({
      // 200-config page: the Configure tab list is not expected to exceed it;
      // the proxy default page size is smaller.
      query: () => `${PROXY_API_V1_URL}/custom-agents/configs?page_size=200`,
      transformResponse: (raw: { custom_agent_configs?: CustomAgentConfig[] }) =>
        normalizeCustomAgentConfigList(raw.custom_agent_configs ?? []),
      providesTags: (result) =>
        result
          ? [
              ...result.map((c) => ({
                type: 'CustomAgentConfig' as const,
                id: c.id
              })),
              { type: 'CustomAgentConfig' as const, id: 'LIST' }
            ]
          : [{ type: 'CustomAgentConfig' as const, id: 'LIST' }]
    }),
    getCustomAgentConfig: builder.query<CustomAgentConfig, string>({
      query: (id) => `${PROXY_API_V1_URL}/custom-agents/configs/${id}`,
      transformResponse: (raw: CustomAgentConfig) => normalizeCustomAgentConfig(raw),
      providesTags: (_r, _e, id) => [{ type: 'CustomAgentConfig', id }]
    }),
    getCustomAgentConfigEvaluation: builder.query<
      CustomAgentConfigEvaluation,
      { customAgentConfigId: string }
    >({
      query: ({ customAgentConfigId }) =>
        `${PROXY_API_V1_URL}/custom-agents/configs/${customAgentConfigId}/evaluate`,
      transformResponse: (response: {
        custom_agent_config_id: string
        evaluated_at: string
        gateway_url?: string
        project?: string
        readings?: BackendTagReading[]
        triggers?: TriggerEvalResult[]
      }): CustomAgentConfigEvaluation => ({
        customAgentConfigId: response.custom_agent_config_id,
        evaluatedAt: response.evaluated_at,
        gatewayUrl: response.gateway_url ?? '',
        project: response.project ?? '',
        readings: asArray<BackendTagReading>(response.readings).map(normalizeTagReading),
        triggers: asArray<TriggerEvalResult>(response.triggers)
      }),
      providesTags: (_r, _e, { customAgentConfigId }) => [
        { type: 'CustomAgentConfig', id: customAgentConfigId }
      ]
    }),
    getCustomAgentTriggerState: builder.query<
      TriggerStateSnapshot,
      { customAgentConfigId: string }
    >({
      query: ({ customAgentConfigId }) =>
        `${PROXY_API_V1_URL}/custom-agents/configs/${customAgentConfigId}/trigger-state`,
      transformResponse: (response: unknown, _meta, { customAgentConfigId }) =>
        normalizeTriggerStateSnapshot(response, customAgentConfigId),
      providesTags: (_r, _e, { customAgentConfigId }) => [
        { type: 'CustomAgentConfig', id: customAgentConfigId }
      ]
    }),
    updateCustomAgentTriggerOccurrence: builder.mutation<
      void,
      {
        customAgentConfigId: string
        occurrenceId: string
        action: 'acknowledge' | 'unacknowledge' | 'suppress' | 'unsuppress'
      }
    >({
      query: ({ customAgentConfigId, occurrenceId, action }) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/configs/${encodeURIComponent(customAgentConfigId)}/trigger-occurrences/${encodeURIComponent(occurrenceId)}:${action}`,
        method: 'POST'
      }),
      invalidatesTags: (_r, _e, { customAgentConfigId }) => [
        { type: 'CustomAgentConfig', id: customAgentConfigId }
      ]
    }),
    createCustomAgentConfig: builder.mutation<CustomAgentConfig, CreateCustomAgentConfigPayload>({
      query: (body) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/configs`,
        method: 'POST',
        body: denormalizeCreateCustomAgentConfigPayload(body)
      }),
      invalidatesTags: [{ type: 'CustomAgentConfig', id: 'LIST' }]
    }),
    updateCustomAgentConfig: builder.mutation<
      CustomAgentConfig,
      { id: string; patch: UpdateCustomAgentConfigPayload }
    >({
      query: ({ id, patch }) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/configs/${id}`,
        method: 'PATCH',
        body: denormalizeUpdateCustomAgentConfigPayload(patch)
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'CustomAgentConfig', id },
        { type: 'CustomAgentConfig', id: 'LIST' }
      ]
    }),
    deleteCustomAgentConfig: builder.mutation<void, string>({
      query: (id) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/configs/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'CustomAgentConfig', id },
        { type: 'CustomAgentConfig', id: 'LIST' }
      ]
    }),
    /** Manually fire a new run for this config (AIP-136 `:run`). */
    dispatchCustomAgentRun: builder.mutation<CustomAgentRun, { configId: string }>({
      query: ({ configId }) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/configs/${configId}:run`,
        method: 'POST'
      }),
      invalidatesTags: (_r, _e, { configId }) => [
        { type: 'CustomAgentConfig', id: configId },
        { type: 'CustomAgentRun', id: 'LIST' }
      ]
    }),
    cancelCustomAgentRun: builder.mutation<CustomAgentRun, string>({
      query: (runId) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/runs/${runId}:cancel`,
        method: 'POST'
      }),
      invalidatesTags: (_r, _e, runId) => [
        { type: 'CustomAgentRun', id: runId },
        { type: 'CustomAgentRun', id: 'LIST' }
      ]
    }),
    getCustomAgentBulkBatch: builder.query<CustomAgentBulkBatch, string>({
      query: (batchId) =>
        `${PROXY_API_V1_URL}/custom-agents/bulk-batches/${encodeURIComponent(batchId)}`,
      providesTags: (_r, _e, batchId) => [{ type: 'CustomAgentBulkBatch', id: batchId }]
    }),
    listRunningCustomAgentBulkBatches: builder.query<CustomAgentBulkBatch[], string>({
      query: (conversationId) => {
        const params = new URLSearchParams({
          triggered_by_conversation_id: conversationId
        })
        return `${PROXY_API_V1_URL}/custom-agents/bulk-batches?${params.toString()}`
      },
      transformResponse: (raw: { custom_agent_bulk_batches?: CustomAgentBulkBatch[] }) =>
        raw.custom_agent_bulk_batches ?? [],
      providesTags: (result) =>
        result
          ? result.map((batch) => ({ type: 'CustomAgentBulkBatch' as const, id: batch.id }))
          : []
    }),
    cancelCustomAgentBulkBatch: builder.mutation<CustomAgentBulkBatch, string>({
      query: (batchId) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/bulk-batches/${encodeURIComponent(batchId)}:cancel`,
        method: 'POST'
      }),
      invalidatesTags: (_r, _e, batchId) => [{ type: 'CustomAgentBulkBatch', id: batchId }]
    }),

    // ─── Custom-Agent Runs ────────────────────────────────────────────────
    listCustomAgentRuns: builder.query<
      CustomAgentRun[],
      {
        custom_agent_config_id?: string
        triggered_by_conversation_id?: string
        trigger_kind?: string
        include?: 'follow_up'
        exclude?: 'bulk'
      } | void
    >({
      query: (args) => {
        const params: Record<string, string> = { page_size: '100' }
        if (args?.custom_agent_config_id)
          params.custom_agent_config_id = args.custom_agent_config_id
        if (args?.triggered_by_conversation_id)
          params.triggered_by_conversation_id = args.triggered_by_conversation_id
        if (args?.trigger_kind) params.trigger_kind = args.trigger_kind
        if (args?.include) params.include = args.include
        if (args?.exclude) params.exclude = args.exclude
        const qs = new URLSearchParams(params).toString()
        return `${PROXY_API_V1_URL}/custom-agents/runs?${qs}`
      },
      transformResponse: (raw: { custom_agent_runs?: CustomAgentRun[] }) =>
        raw.custom_agent_runs ?? [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({
                type: 'CustomAgentRun' as const,
                id: r.id
              })),
              { type: 'CustomAgentRun' as const, id: 'LIST' }
            ]
          : [{ type: 'CustomAgentRun' as const, id: 'LIST' }]
    }),
    getCustomAgentRun: builder.query<CustomAgentRun, string>({
      query: (id) => `${PROXY_API_V1_URL}/custom-agents/runs/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'CustomAgentRun', id }]
    }),

    // ─── Timeseries results ───────────────────────────────────────────────
    /** Assembled emit documents (`view=document`) — one row per completed run. */
    listCustomAgentResults: builder.query<
      CustomAgentResultListPage,
      {
        configId: string
        pageSize?: number
        pageToken?: string
        startTime?: string
        endTime?: string
        runId?: string
        revision?: number
      }
    >({
      query: ({ configId, pageSize, pageToken, startTime, endTime, runId, revision }) => {
        const params = new URLSearchParams()
        params.set('view', 'document')
        if (pageSize) params.set('page_size', String(pageSize))
        if (pageToken) params.set('page_token', pageToken)
        if (startTime) params.set('start_time', startTime)
        if (endTime) params.set('end_time', endTime)
        if (runId) params.set('run_id', runId)
        if (revision != null && revision >= 1) params.set('revision', String(revision))
        return `${PROXY_API_V1_URL}/custom-agents/configs/${configId}/results?${params}`
      },
      transformResponse: (raw: CustomAgentResultListPage) => ({
        results: raw.results ?? [],
        next_page_token: raw.next_page_token
      }),
      // Results append when runs complete — tie them to the run list so a
      // completed run's invalidation refreshes the series.
      providesTags: [{ type: 'CustomAgentRun', id: 'LIST' }]
    }),

    /** Shredded reviewable items (`view=items`) — for Insights / feedback UI. */
    listCustomAgentResultItems: builder.query<
      CustomAgentResultItemListPage,
      {
        configId: string
        pageSize?: number
        pageToken?: string
        startTime?: string
        endTime?: string
        type?: string
        approvalStatus?: string
        runId?: string
        revision?: number
      }
    >({
      query: ({
        configId,
        pageSize,
        pageToken,
        startTime,
        endTime,
        type,
        approvalStatus,
        runId,
        revision
      }) => {
        const params = new URLSearchParams()
        params.set('view', 'items')
        if (pageSize) params.set('page_size', String(pageSize))
        if (pageToken) params.set('page_token', pageToken)
        if (startTime) params.set('start_time', startTime)
        if (endTime) params.set('end_time', endTime)
        if (type) params.set('type', type)
        if (approvalStatus) params.set('approval_status', approvalStatus)
        if (runId) params.set('run_id', runId)
        if (revision != null && revision >= 1) params.set('revision', String(revision))
        return `${PROXY_API_V1_URL}/custom-agents/configs/${configId}/results?${params}`
      },
      transformResponse: (raw: CustomAgentResultItemListPage) => ({
        results: raw.results ?? [],
        next_page_token: raw.next_page_token
      }),
      providesTags: [{ type: 'CustomAgentRun', id: 'LIST' }]
    }),

    /**
     * Every agent's `action_items` result items (tasks), newest first. The
     * proxy lists results per config only, so this fans out over the config
     * list; superseded revisions are dropped.
     */
    listCustomAgentTasks: builder.query<CustomAgentTask[], WorkScope | void>({
      async queryFn(_arg, _api, _extra, baseQuery) {
        const configs = await readWorkPages<CustomAgentConfig>(
          baseQuery,
          `${PROXY_API_V1_URL}/custom-agents/configs`,
          'custom_agent_configs'
        )
        if ('error' in configs) return configs
        const tasks: CustomAgentTask[] = []
        for (const config of configs.data) {
          if (config.workflow_version === 'incident_v2') continue
          const items = await readWorkPages<CustomAgentResultItem>(
            baseQuery,
            `${PROXY_API_V1_URL}/custom-agents/configs/${encodeURIComponent(config.id)}/results?view=items&type=action_items&order=desc`,
            'results'
          )
          if ('error' in items) return items
          tasks.push(
            ...items.data
              .filter((item) => !item.superseded && !item.problem_occurrence_id)
              .map((item) => ({
                ...item,
                source: 'legacy_result' as const,
                config_id: config.id,
                config_name: config.name
              }))
          )
        }
        return { data: tasks.sort((a, b) => b.time.localeCompare(a.time)) }
      },
      providesTags: [{ type: 'CustomAgentTask', id: 'LIST' }]
    }),

    /** Per-item approve / reject / comment. Reject requires feedback. */
    patchCustomAgentResultFeedback: builder.mutation<
      CustomAgentResultItem,
      { id: string } & PatchCustomAgentResultFeedbackPayload
    >({
      query: ({ id, ...body }) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/results/${id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [
        { type: 'CustomAgentRun', id: 'LIST' },
        { type: 'CustomAgentTask', id: 'LIST' }
      ]
    }),

    batchUpdateCustomAgentResults: builder.mutation<
      BatchUpdateCustomAgentResultsResponse,
      BatchUpdateCustomAgentResultsPayload
    >({
      query: (body) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/results:batch`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: (result) => {
        const tags: Array<{ type: 'CustomAgentRun' | 'CustomAgentTask'; id: string }> = [
          { type: 'CustomAgentRun', id: 'LIST' },
          { type: 'CustomAgentTask', id: 'LIST' }
        ]
        const parentId = result?.results[0]?.run_id
        if (parentId) tags.push({ type: 'CustomAgentRun', id: parentId })
        const followUp = result?.follow_up_run
        if (followUp?.id) tags.push({ type: 'CustomAgentRun', id: followUp.id })
        return tags
      }
    }),

    patchCustomAgentRunCompletion: builder.mutation<
      CustomAgentRun,
      { runId: string } & PatchCustomAgentRunCompletionPayload
    >({
      query: ({ runId, ...body }) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/runs/${runId}/completion`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: (_r, _e, { runId }) => [
        { type: 'CustomAgentRun', id: runId },
        { type: 'CustomAgentRun', id: 'LIST' }
      ]
    }),

    followUpCustomAgentRun: builder.mutation<
      CustomAgentRun,
      { parentRunId: string } & FollowUpCustomAgentRunPayload
    >({
      query: ({ parentRunId, ...body }) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/runs/${parentRunId}:follow-up`,
        method: 'POST',
        body
      }),
      invalidatesTags: (_r, _e, { parentRunId }) => [
        { type: 'CustomAgentRun', id: parentRunId },
        { type: 'CustomAgentRun', id: 'LIST' }
      ]
    }),

    listCustomAgentRunMessages: builder.query<CustomAgentRunMessage[], { runId: string }>({
      query: ({ runId }) => `${PROXY_API_V1_URL}/custom-agents/runs/${runId}/messages`,
      transformResponse: (raw: { messages?: CustomAgentRunMessage[] }) => raw.messages ?? [],
      providesTags: (_r, _e, { runId }) => [{ type: 'CustomAgentRun', id: runId }]
    }),

    postCustomAgentRunMessage: builder.mutation<
      CustomAgentRunMessage,
      {
        runId: string
        role: 'operator' | 'agent' | 'system'
        body: string
        item_ids?: string[]
      }
    >({
      query: ({ runId, ...body }) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/runs/${runId}/messages`,
        method: 'POST',
        body
      }),
      invalidatesTags: (_r, _e, { runId }) => [{ type: 'CustomAgentRun', id: runId }]
    }),

    /** Unbind a writeback tool from this config. Last unbind prunes the registry row. */
    unbindCustomAgentWritebackTool: builder.mutation<void, { configId: string; toolId: string }>({
      query: ({ configId, toolId }) => ({
        url: `${PROXY_API_V1_URL}/custom-agents/configs/${encodeURIComponent(configId)}/writeback-tools/${encodeURIComponent(toolId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_r, _e, { configId }) => [
        { type: 'CustomAgentConfig', id: configId },
        { type: 'CustomAgentConfig', id: 'LIST' }
      ]
    })
  })
})

export const {
  useListCustomAgentConfigsQuery,
  useGetCustomAgentConfigQuery,
  useGetCustomAgentConfigEvaluationQuery,
  useGetCustomAgentTriggerStateQuery,
  useUpdateCustomAgentTriggerOccurrenceMutation,
  useCreateCustomAgentConfigMutation,
  useUpdateCustomAgentConfigMutation,
  useDeleteCustomAgentConfigMutation,
  useDispatchCustomAgentRunMutation,
  useCancelCustomAgentRunMutation,
  useGetCustomAgentBulkBatchQuery,
  useListRunningCustomAgentBulkBatchesQuery,
  useCancelCustomAgentBulkBatchMutation,
  useListCustomAgentRunsQuery,
  useLazyListCustomAgentRunsQuery,
  useGetCustomAgentRunQuery,
  usePatchCustomAgentRunCompletionMutation,
  useListCustomAgentResultsQuery,
  useLazyListCustomAgentResultsQuery,
  useListCustomAgentResultItemsQuery,
  useLazyListCustomAgentResultItemsQuery,
  useListCustomAgentTasksQuery,
  usePatchCustomAgentResultFeedbackMutation,
  useBatchUpdateCustomAgentResultsMutation,
  useFollowUpCustomAgentRunMutation,
  useListCustomAgentRunMessagesQuery,
  usePostCustomAgentRunMessageMutation,
  useUnbindCustomAgentWritebackToolMutation
} = customAgentsApi
